/**
 * numericScrub — Nuke-style digit stepping for a number field.
 *
 * Put the text cursor in a number and scroll: the value moves by the place
 * value of the digit just RIGHT of the cursor.
 *
 *   1.|0   → ±0.1   (1.0, 1.1, … 1.9, 2.0)
 *   |1.9   → ±1     (1.9, 2.9, … 9.9, 10.9)
 *   1.9|2  → ±0.01
 *   |10.5  → ±10
 *
 * A `.` or a sign right of the cursor is skipped to the next digit, so 1|.0
 * steps the tenths, the same as 1.|0.
 *
 * With NO digit right of the cursor (it is at the end) the step is one place
 * FINER than the last digit, and that decimal is added: 20.1| → 20.11, 20.12,
 * … (20.09 going down). A field that only takes whole numbers (`minPower` 0)
 * never grows a decimal; there the end steps the last digit.
 *
 * After a step the cursor sits just left of the digit being stepped, so it
 * stays on the same place value when the number grows a digit (9.9 → 10.9
 * keeps stepping by 1) and keeps stepping a decimal it just added (20.1| →
 * 20.1|1). The one exception keeps the cursor at the end: a whole-number field
 * stepped from the end.
 *
 * Pure: no DOM. src/lib/numericScrubInstall.js wires it to the page.
 */

const NUMBER_RE = /^[-+]?(\d+\.?\d*|\.\d+)$/

/**
 * The step for a caret position, or null when the text is not a plain decimal
 * (empty, exponent notation, mid-edit garbage) — then nothing should happen.
 * @param {string} text
 * @param {number} caret  index between characters, 0..text.length
 * @param {number} [minPower]  the finest place allowed (0 = whole numbers);
 *   only limits the extra place the end adds, never a digit already shown
 * @returns {{ power: number, step: number, atEnd: boolean } | null}
 */
export function scrubStep(text, caret, minPower = -Infinity) {
  if (typeof text !== 'string' || !NUMBER_RE.test(text)) return null
  const dot = text.indexOf('.') === -1 ? text.length : text.indexOf('.')
  let at = -1
  for (let i = Math.max(0, caret); i < text.length; i++) {
    if (text[i] >= '0' && text[i] <= '9') { at = i; break }
  }
  const atEnd = at === -1
  if (atEnd) {
    for (let i = text.length - 1; i >= 0; i--) {
      if (text[i] >= '0' && text[i] <= '9') { at = i; break }
    }
  }
  if (at === -1) return null
  let power = at < dot ? dot - at - 1 : -(at - dot)
  // At the end: one place finer than the last digit, if the field allows it.
  if (atEnd && power - 1 >= minPower) power -= 1
  return { power, step: Math.pow(10, power), atEnd }
}

/**
 * Step `text` once in `direction` (+1 up, -1 down) from the caret.
 * @param {string} text
 * @param {number} caret
 * @param {1|-1} direction
 * @param {{ min?: number, max?: number, minPower?: number }} [limits]
 * @returns {{ text: string, caret: number, value: number } | null}
 */
export function scrubValue(text, caret, direction, limits = {}) {
  const s = scrubStep(text, caret, limits.minPower)
  if (!s) return null
  const dot = text.indexOf('.')
  const textDecimals = dot === -1 ? 0 : text.length - dot - 1
  const decimals = Math.max(textDecimals, s.power < 0 ? -s.power : 0)
  let value = Number((parseFloat(text) + direction * s.step).toFixed(decimals))
  if (Number.isFinite(limits.min) && value < limits.min) value = limits.min
  if (Number.isFinite(limits.max) && value > limits.max) value = limits.max
  // toFixed(0) of -0 is "0"; Object.is guard keeps "-0.0" from appearing.
  if (Object.is(value, -0)) value = 0
  const next = value.toFixed(decimals)
  return { text: next, caret: caretBefore(next, s.power, s.atEnd), value }
}

// The index just left of the digit at `power` in `text` — where the cursor
// goes after a step. A whole-number step from the end keeps the cursor at the
// end, where it was.
function caretBefore(text, power, atEnd) {
  if (atEnd && power === 0 && text.indexOf('.') === -1) return text.length
  const dot = text.indexOf('.') === -1 ? text.length : text.indexOf('.')
  const i = power >= 0 ? dot - power - 1 : dot - power
  return Math.max(0, Math.min(text.length, i))
}

/**
 * Turn wheel deltas into whole steps. A mouse notch (one event of at least
 * `pxPerStep`, typically 100-120 px) is exactly ONE step, whatever its size, so
 * a notch never jumps two places. A trackpad sends many small deltas; those
 * accumulate and step once per `pxPerStep`. Returns a stateful function: feed
 * it each event's deltaY (pixels), get the signed steps to take now
 * (positive = scroll UP = increment).
 */
export function createWheelAccumulator(pxPerStep = 50) {
  let acc = 0
  return (deltaPx) => {
    if (Math.abs(deltaPx) >= pxPerStep) {
      acc = 0
      return deltaPx < 0 ? 1 : -1
    }
    acc += -deltaPx
    const steps = (acc > 0 ? Math.floor(acc / pxPerStep) : Math.ceil(acc / pxPerStep)) || 0
    acc -= steps * pxPerStep
    return steps
  }
}

// ── Drag slider (Unreal-style) ───────────────────────────────────────────────
// Press-and-drag on an unfocused number field slides its value; see
// numericScrubInstall.js for the gesture. These are the numbers behind it.

/** Pixels of slow mouse travel per step. */
export const SLIDER_PX_PER_STEP = 4

/**
 * The slider's step: the value's own precision ("1.25" moves in 0.01s, "1.0"
 * in 0.1s, "3" in 1s). A whole-number value in a field whose `step` attribute
 * is fractional takes that attribute's precision instead, so "0" in a
 * step="0.1" field slides in tenths rather than leaping by 1.
 * @param {string} text
 * @param {number} [stepAttr]
 * @returns {{ step: number, decimals: number }}
 */
export function sliderStep(text, stepAttr) {
  const decimalsOf = (s) => {
    const i = String(s).indexOf('.')
    return i === -1 ? 0 : String(s).length - i - 1
  }
  let decimals = NUMBER_RE.test(text ?? '') ? decimalsOf(text) : 0
  if (decimals === 0 && Number.isFinite(stepAttr) && stepAttr > 0 && stepAttr < 1) {
    decimals = decimalsOf(stepAttr)
  }
  return { step: Math.pow(10, -decimals), decimals }
}

/**
 * Speed-proportional gain, like mouse acceleration: 1x up to a slow, careful
 * drag (0.25 px/ms, 250 px/s), then rising linearly, capped at 20x for a flick.
 * @param {number} pxPerMs  smoothed horizontal mouse speed
 */
export function sliderGain(pxPerMs) {
  const g = 1 + Math.max(0, Math.abs(pxPerMs) - 0.25) * 8
  return Math.min(20, g)
}

/**
 * The slider's value after `units` whole steps from `start`, clamped and
 * formatted to `decimals` places.
 * @returns {{ text: string, value: number }}
 */
export function sliderValue(start, units, step, decimals, limits = {}) {
  let value = Number((start + units * step).toFixed(decimals))
  if (Number.isFinite(limits.min) && value < limits.min) value = limits.min
  if (Number.isFinite(limits.max) && value > limits.max) value = limits.max
  if (Object.is(value, -0)) value = 0
  return { text: value.toFixed(decimals), value }
}
